import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { IoSaveOutline } from "react-icons/io5";
import { FaCircleNotch } from "react-icons/fa";
import { tinFormat } from "./../../../../../utils/stringUtils";
import ReactSelect from "./../../../../../components/common/ReactSelect";
import Textbox from "./../../../../../components/common/Textbox";
import TextboxMask from "./../../../../../components/common/TextboxMask";
import Toaster from "./../../../../../components/common/Toaster";
import taxService from "./../../../../../services/taxService";
import _ from "lodash";

const schema = yup.object({
  id: yup.string(),
  number: yup.string().required("Please enter taxpayer identification no."),
  registered_address: yup.string().required("Please enter registered address."),
  revenue_district_office: yup
    .object()
    .shape({
      label: yup.string(),
      value: yup.string(),
    })
    .nullable(),
});

const TaxpayerForm = ({ id, data, isLoading, setLoading, onSuccess }) => {
  const [rdos, setRdos] = useState([]);

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      data["id"] = id;
      await taxService.saveTaxpayerInfoAsync(data);
      Toaster.Success("Information Saved Successfully");
      onSuccess();
    } catch (ex) {
      Toaster.Error("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    const fetchRdos = async () => {
      const data = await taxService.getRevenueDistrictOfficesAsync();
      const options = data.map((item) => {
        const option = {};
        option["label"] = item;
        option["value"] = item;

        return option;
      });

      if (mounted) setRdos(options);
    };

    fetchRdos();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const populateForm = () => {
      const selected_rdo = {
        label: data.revenue_district_office,
        value: data.revenue_district_office,
      };

      setValue("number", data.number);
      setValue("registered_address", data.registered_address);
      setValue("revenue_district_office", selected_rdo);
    };

    if (!_.isEmpty(data)) populateForm();
  }, [data, setValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <TextboxMask
            id="number-textbox"
            name="number"
            type="tel"
            title="Taxpayer Identification No."
            format={tinFormat}
            setValue={setValue}
            value={data.number}
            register={register("number")}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <Textbox
            id="address-textbox"
            name="registered_address"
            title="Registered Address"
            type="text"
            register={register("registered_address")}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <ReactSelect
            id="rdo-dropdown"
            name="revenue_district_office"
            title="Revenue District Office"
            options={rdos}
            control={control}
            register={register("revenue_district_office")}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-2 pt-2">
        <div className="col-span-2">
          <button
            type="submit"
            className="button button-primary disabled:opacity-70 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading && <FaCircleNotch className="animate-spin mr-3" />}
            {!isLoading && <IoSaveOutline className="mr-3" />}
            Save Changes
          </button>
        </div>
      </div>
    </form>
  );
};

export default TaxpayerForm;
